import type {
  FieldValues,
  IssueFieldSelectionValues,
  IssueFieldSingleSelectOption,
  IssueFieldType,
} from '@github-ui/issue-fields-shared/issue-field'
import type {EntityOperation} from '@github-ui/optimistic-connection'
import type {IssueFieldDataType, IssueFieldVisibility} from '@github-ui/server-persisted-queries/types/IssueViewerPage'

export type OptimisticIssueFieldValue = {
  __typename:
    | 'IssueFieldTextValue'
    | 'IssueFieldSingleSelectValue'
    | 'IssueFieldMultiSelectValue'
    | 'IssueFieldDateValue'
    | 'IssueFieldNumberValue'
  field: {
    id: string
    name: string
    dataType?: IssueFieldDataType
    visibility?: IssueFieldVisibility
  }
  value?: string | null
  optionId?: string | null
  name?: string | null
  color?: IssueFieldSingleSelectOption['color'] | null
  description?: string | null
  options?: Array<{
    id?: string
    name: string
    color?: IssueFieldSingleSelectOption['color'] | null
    description?: string | null
  }>
  multiSelectValue?: string | null
  numberValue?: number | null
}

export type IssueFieldValueMutationInput = {
  issueId: string
  issueField: {fieldId: string} & FieldValues
}

type UpsertIssueFieldValueOperation = Extract<EntityOperation, {type: 'upsertEntityFieldItem'}>
type RemoveIssueFieldValueOperation = Extract<EntityOperation, {type: 'removeEntityFieldItem'}>

const fieldTypeToRelayDataType = {
  text: 'TEXT',
  single_select: 'SINGLE_SELECT',
  multi_select: 'MULTI_SELECT',
  date: 'DATE',
  number: 'NUMBER',
} as const satisfies Record<IssueFieldType, IssueFieldDataType>

const typenameToRelayDataType = {
  IssueFieldTextValue: 'TEXT',
  IssueFieldSingleSelectValue: 'SINGLE_SELECT',
  IssueFieldMultiSelectValue: 'MULTI_SELECT',
  IssueFieldDateValue: 'DATE',
  IssueFieldNumberValue: 'NUMBER',
} as const satisfies Record<OptimisticIssueFieldValue['__typename'], IssueFieldDataType>

export function upsertIssueFieldValue(
  issueId: string,
  value: OptimisticIssueFieldValue,
): UpsertIssueFieldValueOperation {
  return {
    type: 'upsertEntityFieldItem',
    id: issueId,
    field: 'issueFieldValues',
    key: value.field.id,
    item: toIssueFieldValueNode(value),
  }
}

export function removeIssueFieldValue(
  issueId: string,
  fieldId: string,
  fieldName: string,
): RemoveIssueFieldValueOperation {
  return {
    type: 'removeEntityFieldItem',
    id: issueId,
    field: 'issueFieldValues',
    key: fieldId,
    item: {field: {id: fieldId, name: fieldName}},
  }
}

export function toOptimisticIssueFieldValue(
  fieldId: string,
  fieldName: string,
  fieldType: IssueFieldType,
  selection: IssueFieldSelectionValues,
): OptimisticIssueFieldValue | undefined {
  const dataType = toRelayFieldDataType(fieldType)
  const field = {id: fieldId, name: fieldName, dataType}
  switch (fieldType) {
    case 'text':
      return typeof selection === 'string' ? {__typename: 'IssueFieldTextValue', field, value: selection} : undefined
    case 'single_select': {
      const option = selection as IssueFieldSingleSelectOption | null
      return option && typeof option.name === 'string'
        ? {
            __typename: 'IssueFieldSingleSelectValue',
            field,
            optionId: option.id,
            name: option.name,
            color: option.color,
            description: option.description ?? null,
          }
        : undefined
    }
    case 'multi_select': {
      if (!Array.isArray(selection) || !selection.every(option => typeof option.name === 'string')) return undefined
      return {
        __typename: 'IssueFieldMultiSelectValue',
        field,
        multiSelectValue: selection.map(option => option.name).join(','),
        options: selection.map(option => ({
          id: option.id,
          name: option.name,
          color: option.color,
          description: option.description ?? null,
        })),
      }
    }
    case 'date':
      return selection instanceof Date
        ? {__typename: 'IssueFieldDateValue', field, value: selection.toISOString()}
        : undefined
    case 'number':
      return typeof selection === 'number'
        ? {__typename: 'IssueFieldNumberValue', field, numberValue: selection}
        : undefined
  }
}

export function normalizeIssueFieldValue(
  value: unknown,
  optimisticValue: OptimisticIssueFieldValue,
): OptimisticIssueFieldValue | undefined {
  if (!value || typeof value !== 'object') return undefined
  const record = value as Record<string, unknown>
  const {__typename, field} = optimisticValue

  switch (__typename) {
    case 'IssueFieldTextValue':
      return typeof record.value === 'string' || record.value === null
        ? {__typename, field, value: record.value}
        : undefined
    case 'IssueFieldSingleSelectValue':
      return typeof record.name === 'string' || record.name === null
        ? {
            __typename,
            field,
            optionId:
              typeof record.optionId === 'string' || record.optionId === null
                ? record.optionId
                : (optimisticValue.optionId ?? null),
            name: record.name,
            color:
              typeof record.color === 'string' || record.color === null
                ? (record.color as OptimisticIssueFieldValue['color'])
                : (optimisticValue.color ?? null),
            description:
              typeof record.description === 'string' || record.description === null
                ? record.description
                : (optimisticValue.description ?? null),
          }
        : undefined
    case 'IssueFieldMultiSelectValue': {
      if (!Array.isArray(record.options)) return undefined
      const normalizedOptions = record.options.map(option => {
        if (!option || typeof option !== 'object') return undefined
        const recordOption = option as Record<string, unknown>
        if (typeof recordOption.name !== 'string') return undefined
        return {
          id: typeof recordOption.id === 'string' ? recordOption.id : undefined,
          name: recordOption.name,
          color:
            typeof recordOption.color === 'string' || recordOption.color === null
              ? (recordOption.color as OptimisticIssueFieldValue['color'])
              : undefined,
          description:
            typeof recordOption.description === 'string' || recordOption.description === null
              ? recordOption.description
              : undefined,
        }
      })
      if (normalizedOptions.some(option => !option)) return undefined
      return {
        __typename,
        field,
        options: normalizedOptions as NonNullable<OptimisticIssueFieldValue['options']>,
        multiSelectValue:
          typeof record.multiSelectValue === 'string' || record.multiSelectValue === null
            ? record.multiSelectValue
            : (optimisticValue.multiSelectValue ?? null),
      }
    }
    case 'IssueFieldDateValue':
      return typeof record.value === 'string' || record.value === null
        ? {__typename, field, value: record.value}
        : undefined
    case 'IssueFieldNumberValue':
      return typeof record.numberValue === 'number' || record.numberValue === null
        ? {__typename, field, numberValue: record.numberValue}
        : undefined
  }
}

function toRelayFieldDataType(fieldType: IssueFieldType): OptimisticIssueFieldValue['field']['dataType'] {
  return fieldTypeToRelayDataType[fieldType]
}

function toRelayDataTypeFromTypename(
  typename: OptimisticIssueFieldValue['__typename'],
): NonNullable<OptimisticIssueFieldValue['field']['dataType']> {
  return typenameToRelayDataType[typename]
}

function toIssueFieldValueNode(value: OptimisticIssueFieldValue): Record<string, unknown> {
  const fieldDataType = value.field.dataType ?? toRelayDataTypeFromTypename(value.__typename)
  const field = {
    id: value.field.id,
    name: value.field.name,
    dataType: fieldDataType,
    ...(value.field.visibility ? {visibility: value.field.visibility} : {}),
  }
  switch (value.__typename) {
    case 'IssueFieldTextValue':
    case 'IssueFieldDateValue':
      return {__typename: value.__typename, field, value: value.value ?? null}
    case 'IssueFieldSingleSelectValue':
      return {
        __typename: value.__typename,
        field,
        optionId: value.optionId ?? null,
        name: value.name ?? null,
        color: value.color ?? null,
        description: value.description ?? null,
      }
    case 'IssueFieldMultiSelectValue':
      return {
        __typename: value.__typename,
        field,
        options: (value.options ?? []).map(option => ({
          ...(option.id ? {id: option.id} : {}),
          name: option.name,
          color: option.color ?? null,
          description: option.description ?? null,
        })),
        multiSelectValue: value.multiSelectValue ?? null,
      }
    case 'IssueFieldNumberValue':
      return {__typename: value.__typename, field, numberValue: value.numberValue ?? null}
  }
}
